import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, User, Calendar, Eye, Heart, Share2, Bookmark, Tag, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { blogApi, BlogPost, BlogPostSummary } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { SEO, absoluteUrl, breadcrumbJsonLd } from "@/lib/seo";
import { marked } from "marked";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";


const BlogPostPage = () => {
    const { lang } = useLanguage();
    const localizedPath = useLocalizedPath();
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [post, setPost] = useState<BlogPost | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<BlogPostSummary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLiked, setIsLiked] = useState(false);

    useEffect(() => {
        if (slug) {
            loadPost();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, lang]);

    const loadPost = async () => {
        setIsLoading(true);
        try {
            const response = await blogApi.getPostBySlug(slug!, { lang });
            const postData = response.data.data;
            setPost(postData);

            // Increment views
            if (postData) {
                blogApi.incrementViews(postData.id).catch(() => { });

                // Load related posts
                const relatedResponse = await blogApi.getRelatedPosts(postData.id, { limit: 3, lang });
                setRelatedPosts(relatedResponse.data.data?.posts || []);
            }
        } catch (error) {
            console.error('Failed to load post:', error);
            navigate(localizedPath('/blog'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleLike = async () => {
        if (!post || isLiked) return;
        try {
            const response = await blogApi.likePost(post.id);
            setPost({ ...post, likes: response.data.data?.likes || post.likes + 1 });
            setIsLiked(true);
            toast({ title: "شكراً لإعجابك!" });
        } catch (error) {
            toast({ title: "حدث خطأ", variant: "destructive" });
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: post?.title,
                    text: post?.excerpt,
                    url: url
                });
            } catch (error) {
                // User cancelled
            }
        } else {
            await navigator.clipboard.writeText(url);
            toast({ title: "تم نسخ الرابط!" });
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('ar-SY', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Parse markdown content to HTML
    const parsedContent = useMemo(() => {
        if (!post?.content) return '';

        // Configure marked options
        marked.setOptions({
            breaks: true, // Convert \n to <br>
            gfm: true, // GitHub Flavored Markdown
        });

        return marked.parse(post.content);
    }, [post?.content]);

    if (isLoading) {
        return (
            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[100svh] flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
        );
    }

    if (!post) {
        return (
            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[100svh]">
                <div className="container mx-auto px-6 text-center">
                    <h1 className="text-4xl font-black text-white mb-4">المقالة غير موجودة</h1>
                    <p className="text-muted-foreground mb-8">عذراً، لم نتمكن من العثور على هذه المقالة.</p>
                    <Link to="/blog">
                        <Button>العودة للمدونة</Button>
                    </Link>
                </div>
            </div>
        );
    }

    const postPath = `/blog/${post.slug}`;
    const postUrl = post.canonicalUrl || absoluteUrl(postPath);
    const postImage = post.ogImage || post.featuredImage;

    const articleJsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.metaDescription || post.excerpt,
        image: postImage,
        author: {
            "@type": "Person",
            name: post.author.name,
            jobTitle: post.author.role,
        },
        publisher: {
            "@type": "Organization",
            name: "TRBD",
            logo: {
                "@type": "ImageObject",
                url: absoluteUrl("/og-image.png"),
            },
        },
        datePublished: post.publishedAt,
        dateModified: post.updatedAt,
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": postUrl,
        },
        articleSection: post.category.name,
        keywords: post.metaKeywords?.join(", ") || post.tags.join(", "),
        wordCount: post.content.split(/\s+/).length,
        interactionStatistic: [
            {
                "@type": "InteractionCounter",
                interactionType: "https://schema.org/LikeAction",
                userInteractionCount: post.likes,
            },
            {
                "@type": "InteractionCounter",
                interactionType: "https://schema.org/ViewAction",
                userInteractionCount: post.views,
            },
        ],
    };

    return (
        <>
            <SEO
                title={`${post.metaTitle || post.title} | TRBD Blog`}
                description={post.metaDescription || post.excerpt}
                path={postPath}
                keywords={post.metaKeywords?.length ? post.metaKeywords : post.tags}
                ogImage={postImage}
                ogType="article"
                publishedTime={post.publishedAt}
                modifiedTime={post.updatedAt}
                author={post.author.name}
                section={post.category.name}
                tags={post.tags}
                jsonLd={[
                    breadcrumbJsonLd([
                        { name: "الرئيسية", path: "/" },
                        { name: "المدونة", path: "/blog" },
                        { name: post.title, path: postPath },
                    ]),
                    articleJsonLd,
                ]}
            />

            <article className="pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[100svh]" itemScope itemType="https://schema.org/BlogPosting">
                <div className="container mx-auto px-6">
                    {/* Back Button */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="mb-12"
                    >
                        <Link
                            to="/blog"
                            className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors font-bold"
                        >
                            <ArrowRight size={20} />
                            العودة للمدونة
                        </Link>
                    </motion.div>

                    {/* Hero Image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative h-[400px] md:h-[500px] rounded-[3rem] overflow-hidden mb-12"
                    >
                        <img
                            src={post.featuredImage}
                            alt={post.title}
                            className="absolute inset-0 w-full h-full object-cover"
                            itemProp="image"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
                            <Link
                                to={`/blog?category=${post.category.slug}`}
                                className="inline-block px-4 py-2 bg-primary text-background text-xs font-black uppercase tracking-widest rounded-full mb-6 hover:bg-primary/80 transition-colors"
                                itemProp="articleSection"
                            >
                                {post.category.name}
                            </Link>
                            <h1
                                className="text-3xl md:text-5xl font-black text-white leading-tight rtl:leading-[1.5] max-w-4xl"
                                itemProp="headline"
                            >
                                {post.title}
                            </h1>
                        </div>
                    </motion.div>

                    <div className="grid lg:grid-cols-12 gap-12">
                        {/* Main Content */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="lg:col-span-8"
                        >
                            {/* Meta */}
                            <div className="flex flex-wrap items-center gap-6 mb-12 pb-8 border-b border-white/10">
                                <div
                                    className="flex items-center gap-3"
                                    itemProp="author"
                                    itemScope
                                    itemType="https://schema.org/Person"
                                >
                                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary overflow-hidden">
                                        {post.author.avatar ? (
                                            <img src={post.author.avatar} alt={post.author.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <User size={20} />
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-bold text-white" itemProp="name">{post.author.name}</p>
                                        <p className="text-sm text-muted-foreground" itemProp="jobTitle">{post.author.role}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Calendar size={16} />
                                    <time dateTime={post.publishedAt} itemProp="datePublished">
                                        {formatDate(post.publishedAt)}
                                    </time>
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Clock size={16} />
                                    <span>{post.readTime} دقيقة قراءة</span>
                                </div>
                                <div className="flex items-center gap-4 text-muted-foreground">
                                    <span className="flex items-center gap-1"><Eye size={16} /> {post.views}</span>
                                    <span className="flex items-center gap-1"><Heart size={16} /> {post.likes}</span>
                                </div>
                            </div>

                            {/* Article Content */}
                            <div
                                className="prose prose-invert prose-lg max-w-none
                                    prose-headings:font-black prose-headings:text-white
                                    prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                                    prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                                    prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-6
                                    prose-strong:text-white prose-strong:font-bold
                                    prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                                    prose-li:mb-2
                                    prose-code:bg-white/10 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-primary
                                    prose-pre:bg-white/5 prose-pre:border prose-pre:border-white/10 prose-pre:rounded-2xl prose-pre:p-6 prose-pre:overflow-x-auto
                                    prose-blockquote:border-primary prose-blockquote:text-muted-foreground prose-blockquote:bg-white/5 prose-blockquote:rounded-xl prose-blockquote:p-4
                                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                                    prose-img:rounded-2xl"
                                itemProp="articleBody"
                                dangerouslySetInnerHTML={{ __html: parsedContent as string }}
                            />


                            {/* Tags */}
                            {post.tags.length > 0 && (
                                <div className="mt-12 pt-8 border-t border-white/10">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Tag size={16} className="text-muted-foreground" />
                                        {post.tags.map((tag, i) => (
                                            <Link
                                                key={i}
                                                to={`/blog?tag=${tag}`}
                                                className="px-3 py-1.5 bg-white/5 rounded-full text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/10 transition-colors"
                                            >
                                                #{tag}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap items-center gap-4">
                                <Button
                                    variant="outline"
                                    className={`gap-2 rounded-xl border-white/10 hover:bg-white/5 ${isLiked ? 'text-red-500 border-red-500/20' : ''}`}
                                    onClick={handleLike}
                                    disabled={isLiked}
                                >
                                    <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
                                    {isLiked ? 'أعجبني!' : 'أعجبني'}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="gap-2 rounded-xl border-white/10 hover:bg-white/5"
                                    onClick={handleShare}
                                >
                                    <Share2 size={18} />
                                    مشاركة
                                </Button>
                            </div>
                        </motion.div>

                        {/* Sidebar */}
                        <motion.aside
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="lg:col-span-4 space-y-8"
                        >
                            {/* Author Card */}
                            <div className="bento-card p-8">
                                <h3 className="text-sm font-black text-white/40 uppercase tracking-widest mb-6">عن الكاتب</h3>
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center text-primary overflow-hidden">
                                        {post.author.avatar ? (
                                            <img src={post.author.avatar} alt={post.author.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <User size={28} />
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-bold text-white text-lg">{post.author.name}</p>
                                        <p className="text-sm text-muted-foreground">{post.author.role}</p>
                                    </div>
                                </div>
                                {post.author.bio && (
                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        {post.author.bio}
                                    </p>
                                )}
                            </div>

                            {/* Related Articles */}
                            {relatedPosts.length > 0 && (
                                <div className="bento-card p-8">
                                    <h3 className="text-sm font-black text-white/40 uppercase tracking-widest mb-6">مقالات ذات صلة</h3>
                                    <div className="space-y-4">
                                        {relatedPosts.map((relatedPost) => (
                                            <Link
                                                key={relatedPost.id}
                                                to={`/blog/${relatedPost.slug}`}
                                                className="block group"
                                            >
                                                <p className="font-bold text-white group-hover:text-primary transition-colors line-clamp-2 mb-1">
                                                    {relatedPost.title}
                                                </p>
                                                <p className="text-xs text-muted-foreground">{relatedPost.readTime} دقيقة</p>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* CTA */}
                            <div className="bento-card p-8 bg-gradient-to-br from-primary/20 to-transparent">
                                <h3 className="text-xl font-black text-white mb-4">شارك معرفتك</h3>
                                <p className="text-muted-foreground text-sm mb-6">
                                    هل لديك خبرة تقنية تريد مشاركتها؟ اكتب مقالتك وسيقوم فريقنا بمراجعتها.
                                </p>
                                <Link to="/blog/submit">
                                    <Button className="w-full bg-white text-background hover:bg-neutral-200 font-bold rounded-xl">
                                        اكتب مقالة
                                    </Button>
                                </Link>
                            </div>
                        </motion.aside>
                    </div>
                </div>
            </article>
        </>
    );
};

export default BlogPostPage;
